import { useEffect, useState } from 'react';
import type { Profesional, SolicitudHorario } from '../../api/ops/citas';
import type { VistaPrevia } from '../../api/ops/disponibilidad';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { estadoDeError } from '../../logica/estados';
import { DIAS, aMinutos, semanaDeTramos, sumarDias, tramosDeSemana, type Rango, type Semana } from '../../logica/horario';
import type { ContextoAgenda } from './contexto';
import { diaCorto, diaLargo, hm, horaEn, mayuscula, textoDeError, tramosDelDia } from './logica';
import { AYUDA } from './textos';

/**
 * «Mi horario» de Mi agenda (9.8, G86): la semana tipo del profesional. La edita solo si el negocio lo deja
 * (`professional_can_edit_schedule`): el cambio vale desde la fecha que elija; antes de guardarlo se mira qué citas deja
 * fuera (`previewScheduleChange`). Si deja alguna, no se aplica solo: se envía con su `change_id` y lo confirma el negocio
 * (G86, G254; ver el informe). Si no la puede editar, la ve y pide un cambio al encargado con «Pedir un cambio»
 * (`requestScheduleChange`, G846): un texto y, si quiere, fechas.
 */
const HORAS = (() => {
    const o: { valor: string; texto: string }[] = [];
    for (let m = 7 * 60; m <= 22 * 60; m += 15) {
        o.push({ valor: String(m), texto: hm(m) });
    }
    return o;
})();

const vacia = (): Semana => [[], [], [], [], [], [], []];
const textoTramos = (t: readonly Rango[]) => (t.length === 0 ? 'No trabajo' : t.map((r) => `${hm(r.desde)} – ${hm(r.hasta)}`).join(' y '));
const igual = (a: Semana, b: Semana) => JSON.stringify(a) === JSON.stringify(b);

export function MiHorario({ ctx, propio, editable = true, registrar }: { ctx: ContextoAgenda; propio: Profesional; editable?: boolean; registrar: (guardar: (() => void) | null, cambiado: boolean) => void }) {
    const avisar = useAvisoBreve();
    const hoy = ctx.ahora.fecha;
    const [guardada, setGuardada] = useState<Semana | null>(null);
    const [semana, setSemana] = useState<Semana>(vacia);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [desde, setDesde] = useState(() => sumarDias(hoy, 6));
    const [dia, setDia] = useState<number | null>(null);
    const [impacto, setImpacto] = useState<VistaPrevia | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [pendiente, setPendiente] = useState<string | null>(null);
    const [pedir, setPedir] = useState(false);

    useEffect(() => {
        let vivo = true;
        const cargar = async (): Promise<Semana> => {
            const l = await ctx.ops.listWeeklySchedules(ctx.negocio, propio.calendar_id, { limit: 20 });
            const vigente = l.data.find((s) => s.status === 'current');
            if (vigente !== undefined && !vigente.inherits) {
                return semanaDeTramos(vigente.intervals);
            }
            // Hereda del negocio: la semana que sale de sus horas efectivas de los próximos 7 días.
            const h = await ctx.ops.getEffectiveHours(ctx.negocio, propio.calendar_id, hoy, sumarDias(hoy, 6));
            const s = vacia();
            for (let i = 0; i < 7; i++) {
                const f = sumarDias(hoy, i);
                const d = (new Date(`${f}T12:00:00Z`).getUTCDay() + 6) % 7;
                s[d] = tramosDelDia(h, f).map(([a, b]) => ({ desde: a, hasta: b }));
            }
            return s;
        };
        cargar()
            .then((s) => {
                if (vivo) {
                    setGuardada(s);
                    setSemana(s);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, propio.calendar_id, hoy, intento]);

    const cambiado = guardada !== null && !igual(semana, guardada);
    const peticion = { valid_from: desde, inherits: false, intervals: tramosDeSemana(semana) };

    const guardar = () => {
        if (!cambiado) {
            avisar({ tipo: 'ok', texto: 'No has cambiado nada todavía.' });
            return;
        }
        setOcupado(true);
        ctx.ops
            .previewScheduleChange(ctx.negocio, { kind: 'weekly_schedule', calendar_id: propio.calendar_id, weekly_schedule: peticion })
            .then((v) => {
                if (v.affected_count > 0) {
                    setImpacto(v);
                    return undefined;
                }
                return ctx.ops.createWeeklySchedule(ctx.negocio, propio.calendar_id, peticion).then(() => {
                    setGuardada(semana);
                    avisar({ tipo: 'ok', texto: `Horario guardado. Vale desde el ${diaLargo(desde).toLocaleLowerCase('es')}.` });
                });
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e) });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    useEffect(() => {
        registrar(ctx.soloLectura || !editable ? null : guardar, cambiado);
    });
    useEffect(() => () => { registrar(null, false); }, [registrar]);

    if (guardada === null) {
        return error !== null ? <EstadoError seccion="tu horario" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={ctx.movil} /> : <EstadoCargando seccion="tu horario" />;
    }

    return (
        <>
            {pendiente !== null && (
                <div className="rv-ag-caja" style={{ background: 'var(--rv-warning-soft)', color: 'var(--rv-warning-ink)' }}>
                    <div className="rv-ag-caja__titulo">
                        <Icono nombre="hourglass" />
                        <span>Cambio pedido</span>
                    </div>
                    <p>{pendiente}</p>
                </div>
            )}
            {editable ? (
                <div style={{ maxWidth: 360 }}>
                    <Fecha etiqueta="Desde" valor={desde} min={hoy} onChange={(v) => { if (v !== '') { setDesde(v); } }} ayuda="El horario anterior se guarda hasta la víspera." />
                </div>
            ) : (
                <div className="rv-ag-caja">
                    <div className="rv-ag-caja__titulo">
                        <Icono nombre="lock" />
                        <span>Tu horario lo cambia el negocio</span>
                    </div>
                    <p>Si necesitas cambiar algún día u hora, pídeselo al encargado: le llega un aviso.</p>
                    {!ctx.soloLectura && (
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setPedir(true); }}>
                            <Icono nombre="send" />
                            <span>Pedir un cambio</span>
                        </button>
                    )}
                </div>
            )}
            <div className="rv-ag-semana">
                {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                    const t = semana[d] ?? [];
                    const cambio = JSON.stringify(t) !== JSON.stringify(guardada[d] ?? []);
                    return (
                        <button key={d} type="button" className="rv-ag-semana__d" disabled={ctx.soloLectura || !editable} onClick={() => { setDia(d); }}>
                            <strong>{mayuscula(DIAS[d] ?? '')}</strong>
                            <span style={{ color: t.length > 0 ? 'var(--rv-ink)' : 'var(--rv-fg-3)' }}>
                                {textoTramos(t)}
                                {cambio && <small style={{ display: 'block', color: 'var(--rv-red)', fontWeight: 600 }}>Cambiado, sin guardar</small>}
                            </span>
                            <Icono nombre="chevron-right" />
                        </button>
                    );
                })}
            </div>
            {editable && (
                <>
                    <p className="rv-ag-pie">La pausa es el hueco entre dos tramos. El cambio se guarda con «Guardar horario», desde la fecha que elijas.</p>
                    <p className="rv-ag-pie">Si el cambio deja fuera alguna de tus citas, no se aplica solo: lo confirma el negocio y avisa a las clientas.</p>
                </>
            )}
            {pedir && (
                <PedirCambio
                    ctx={ctx}
                    onCerrar={() => { setPedir(false); }}
                    onHecho={(r) => {
                        setPedir(false);
                        setPendiente(textoCambioPedido(r, hm(ctx.ahora.minutos)));
                        avisar({ tipo: 'ok', texto: 'Enviado al encargado.' });
                    }}
                />
            )}
            {ocupado && <span role="status" className="rv-ag-pie">Guardando…</span>}
            {dia !== null && (
                <EditarDia
                    ctx={ctx}
                    dia={dia}
                    tramos={semana[dia] ?? []}
                    onCerrar={() => { setDia(null); }}
                    onAplicar={(t) => {
                        setSemana((s) => s.map((x, i) => (i === dia ? t : x)));
                        setDia(null);
                    }}
                />
            )}
            <Ventana
                abierta={impacto !== null}
                titulo={impacto === null ? '' : impacto.affected_count === 1 ? 'Este cambio deja fuera 1 cita' : `Este cambio deja fuera ${String(impacto.affected_count)} citas`}
                texto="No puedes moverlas tú. Se lo enviamos al negocio: un encargado confirma el cambio, recoloca las citas y avisa a las clientas."
                cancelar="Volver"
                accion={ctx.movil ? 'Enviar' : 'Enviar al negocio'}
                accionIcono="send"
                ocupado={ocupado}
                ocupadoTexto="Enviando…"
                movil={ctx.movil}
                onCerrar={() => {
                    if (!ocupado) {
                        setImpacto(null);
                    }
                }}
                onAccion={() => {
                    if (impacto === null) {
                        return;
                    }
                    setOcupado(true);
                    ctx.ops
                        .createWeeklySchedule(ctx.negocio, propio.calendar_id, { ...peticion, change_id: impacto.change_id })
                        .then(() => {
                            setImpacto(null);
                            setSemana(guardada);
                            setPendiente(`Lo has enviado hoy a las ${hm(ctx.ahora.minutos)}. Hasta que el negocio lo confirme, sigue valiendo tu horario de ahora.`);
                            avisar({ tipo: 'ok', texto: 'Enviado al negocio para que lo confirme.' });
                        })
                        .catch((e: unknown) => {
                            avisar({ tipo: 'error', texto: textoDeError(e) });
                        })
                        .finally(() => {
                            setOcupado(false);
                        });
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {(impacto?.affected_holds ?? []).slice(0, 6).map((a) => (
                        <div key={a.hold_id} className="rv-ag-mov">
                            <div>
                                <strong>{a.holder_label ?? ''}</strong>
                                <small>{a.local_date !== undefined ? `${mayuscula(diaCorto(a.local_date))}, ${a.local_time !== undefined ? hm(aMinutos(a.local_time)) : horaEn(a.starts_at, ctx.zona)} – ${horaEn(a.ends_at, ctx.zona)}` : ''}</small>
                            </div>
                        </div>
                    ))}
                </div>
            </Ventana>
        </>
    );
}

function EditarDia({ ctx, dia, tramos, onCerrar, onAplicar }: { ctx: ContextoAgenda; dia: number; tramos: readonly Rango[]; onCerrar: () => void; onAplicar: (t: Rango[]) => void }) {
    const [trabaja, setTrabaja] = useState(tramos.length > 0);
    const [lista, setLista] = useState<Rango[]>(() => (tramos.length > 0 ? tramos.map((t) => ({ ...t })) : [{ desde: 600, hasta: 840 }]));
    const [intentado, setIntentado] = useState(false);
    const errores = lista.map((t, i) => (t.hasta <= t.desde ? 'Tiene que terminar después de empezar.' : lista.some((u, j) => j !== i && u.desde < t.hasta && t.desde < u.hasta) ? 'Se junta con otro tramo.' : ''));
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={ctx.movil}
            tamano="formulario"
            icono="clock"
            eyebrow="Mi horario"
            titulo={mayuscula(DIAS[dia] ?? '')}
            subtitulo={textoTramos(tramos)}
            accion="Aplicar"
            onAccion={() => {
                if (trabaja && errores.some((e) => e !== '')) {
                    setIntentado(true);
                    return;
                }
                onAplicar(trabaja ? [...lista].sort((a, b) => a.desde - b.desde) : []);
            }}
        >
            <div className="rv-ag-form">
                <Interruptor etiqueta="Trabajo este día" ayuda={trabaja ? 'Tus tramos de ese día.' : 'Ese día no recibes citas.'} valor={trabaja} onChange={setTrabaja} />
                {trabaja &&
                    lista.map((t, i) => (
                        <div key={i} className="rv-ag-tramo">
                            <Desplegable etiqueta={lista.length > 1 ? `Tramo ${String(i + 1)} · Empieza` : 'Empieza'} valor={String(t.desde)} opciones={HORAS} error={intentado ? errores[i] : ''} onChange={(v) => { setLista((l) => l.map((u, j) => (j === i ? { ...u, desde: Number(v) } : u))); }} />
                            <Desplegable etiqueta="Termina" valor={String(t.hasta)} opciones={HORAS} onChange={(v) => { setLista((l) => l.map((u, j) => (j === i ? { ...u, hasta: Number(v) } : u))); }} />
                            {lista.length > 1 && (
                                <button type="button" className="rv-bi" aria-label="Quitar este tramo" onClick={() => { setLista((l) => l.filter((_, j) => j !== i)); }}>
                                    <Icono nombre="x" />
                                </button>
                            )}
                        </div>
                    ))}
                {trabaja && (
                    <div>
                        <button
                            type="button"
                            className="rv-ag-b"
                            onClick={() => {
                                setLista((l) => {
                                    const ultimo = l[l.length - 1];
                                    return [...l, ultimo === undefined ? { desde: 600, hasta: 840 } : { desde: Math.min(ultimo.hasta + 60, 21 * 60), hasta: Math.min(ultimo.hasta + 180, 22 * 60) }];
                                });
                            }}
                        >
                            <Icono nombre="plus" />
                            <span>Añadir tramo</span>
                        </button>
                    </div>
                )}
                <p className="rv-ag-pie">{AYUDA.dia.map((a) => a.texto).join(' ')}</p>
            </div>
        </PanelLateral>
    );
}

/** «Pedir un cambio» (G846): qué quiere cambiar y, si quiere, desde y hasta cuándo; lo recibe el encargado en la campana. */
function PedirCambio({ ctx, onCerrar, onHecho }: { ctx: ContextoAgenda; onCerrar: () => void; onHecho: (r: SolicitudHorario) => void }) {
    const avisar = useAvisoBreve();
    const [texto, setTexto] = useState('');
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const errorTexto = texto.trim().length < 3 ? 'Cuenta qué quieres cambiar.' : '';
    const errorHasta = desde !== '' && hasta !== '' && hasta < desde ? 'Tiene que ser después de «Desde».' : '';
    return (
        <PanelLateral
            abierto
            onCerrar={() => { if (!ocupado) { onCerrar(); } }}
            movil={ctx.movil}
            tamano="formulario"
            icono="send"
            eyebrow="Mi horario"
            titulo="Pedir un cambio"
            subtitulo="Se lo enviamos al encargado. Tu horario no cambia hasta que lo cambie él."
            accion="Enviar"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Enviando…"
            onAccion={() => {
                if (errorTexto !== '' || errorHasta !== '') {
                    setIntentado(true);
                    return;
                }
                setOcupado(true);
                ctx.ops
                    .requestScheduleChange(ctx.negocio, { message: texto.trim(), ...(desde === '' ? {} : { from: desde }), ...(hasta === '' ? {} : { to: hasta }) })
                    .then(onHecho)
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e) });
                    })
                    .finally(() => {
                        setOcupado(false);
                    });
            }}
        >
            <div className="rv-ag-form">
                <Campo etiqueta="Qué quieres cambiar" valor={texto} onChange={setTexto} filas={4} max={1000} mayusculas="sentences" error={intentado ? errorTexto : ''} ayuda="Por ejemplo: «Los jueves salgo a las 18:00»." />
                <Fecha etiqueta="Desde (opcional)" valor={desde} min={ctx.ahora.fecha} onChange={setDesde} />
                <Fecha etiqueta="Hasta (opcional)" valor={hasta} min={desde === '' ? ctx.ahora.fecha : desde} onChange={setHasta} error={intentado ? errorHasta : ''} />
            </div>
        </PanelLateral>
    );
}

/**
 * La tarjeta «Cambio pedido» (9.8, G846, G911): lo pedido tal como lo guarda la API (si ya había una abierta, es la suma de las
 * dos: el texto nuevo tras una línea en blanco y las fechas cubren ambas), la hora y que se cierra al cambiar su horario.
 */
function textoCambioPedido(r: Pick<SolicitudHorario, 'message' | 'from' | 'to'>, hora: string): string {
    const fechas = r.from != null && r.to != null ? `, del ${diaCorto(r.from)} al ${diaCorto(r.to)}` : r.from != null ? `, desde el ${diaCorto(r.from)}` : r.to != null ? `, hasta el ${diaCorto(r.to)}` : '';
    const pedido = (r.message ?? '').replace(/\n\n/g, ' · ').replace(/[.\s]+$/, '');
    return `Lo pediste hoy a las ${hora}${fechas}: «${pedido}». Lo ven tu encargado y los administradores en su campana y se cierra cuando cambien tu horario. Si pides otra cosa antes, se suma a esta petición.`;
}
